import React, { useRef, useMemo } from 'react';
import Icon from '../Icon';
import Button from '../Button';
import styles from './ImageDropzone.module.scss';
import { useTranslation } from 'react-i18next';
import { keys } from '../../utilities/translator/translation_keys';
import { v4 as uuidv4 } from 'uuid';
import { useApolloClient } from '@apollo/client';
import axios from 'axios';
import { loader } from 'graphql.macro';
import { useCurrentUser } from '../../providers/UserProvider';
import { CF_DOMAIN } from '../../constants/aws';
import { Text } from '../typography';
import { MAX_FILE_UPLOAD_SIZE, toastVariant } from '../../constants/misc';
import { useModal } from '../../providers/ModalProvider';
import SimpleFileCard from '../files/SimpleFileCard';
import { Spinner } from 'react-bootstrap';
import { showToast } from '../../graphql/cache/modal';
import { blockedFileTypes } from '../files/utils';
import { convertHeicIfNeeded } from './utils.js';

const uploadUrlQuery = loader('../../graphql/queries/s3.graphql');

export default function QuickFileDropzone({
  onSubmit = () => {},
  setLoading = () => {},
  loading = false,
  icon = false,
  iconText = 'Add File',
  iconImage = 'add',
  attachments = [],
  onDelete = () => {},
  readOnly = false,
  // Lets a caller gate deleting an attachment separately from adding one —
  // e.g. "anyone can attach a file, but only the assignee can remove one."
  // Defaults to mirroring `readOnly` so every existing caller keeps its
  // current behavior unless it explicitly opts into the split.
  canDelete,
  disableAddFile = false,
  iconSize = '1.2rem',
  iconColor = 'primary',
  noAuth = false,
  header = null,
  allowMulti = false,
}) {
  const fileInputRef = useRef(null);
  const iconFileInputRef = useRef(null);
  const pendingUploadsRef = useRef(0);
  const { t } = useTranslation();
  const client = useApolloClient();
  const { user } = useCurrentUser() || {};
  const { openConfirmationModal } = useModal() || {};

  const noAuthUser = useMemo(() => (noAuth ? noAuth : false), [noAuth]);
  const effectiveCanDelete = canDelete ?? !readOnly;

  const handleButtonClick = () => {
    fileInputRef.current.click();
  };

  const handleIconClick = (e) => {
    e.stopPropagation();
    iconFileInputRef.current.click();
  };

  const handleUpload = async (file) => {
    if (!file) return;
    pendingUploadsRef.current += 1;
    setLoading(true);
    const usableFile = await convertHeicIfNeeded(file);
    const name = usableFile.name ?? '';
    const fileUUID = uuidv4();
    const fileParts = name.split('.');
    const fileName = fileUUID;
    const fileType = fileParts[fileParts.length - 1];
    if (blockedFileTypes.includes(fileType)) {
      alert(`This file type is not allowed: ${fileType}`);
      return;
    }
    const size = usableFile.size;
    if (size > MAX_FILE_UPLOAD_SIZE) {
      return openConfirmationModal
        ? openConfirmationModal({
            title: 'Max File Size',
            description: t(keys.files.FILE_SIZE_WARNING, { size: '100MB' }),
            onSubmit: () => setLoading(false),
            buttonText: 'OK',
            variant: 'danger',
          })
        : showToast({
            variant: toastVariant.danger,
            title: 'Error',
            message: 'File size too large',
          });
    }
    client
      .query({
        query: uploadUrlQuery,
        variables: {
          fileName: `assets/${
            !noAuth ? user?.company?.id : noAuthUser
          }/${fileName}.${fileType}`,
          fileType,
        },
      })
      .then((data) => {
        const signedUrl = data.data.simpleStorageUploadUrl;
        const options = {
          headers: {
            'Content-Type': fileType === 'pdf' ? 'application/pdf' : fileType,
          },
        };
        return axios.put(signedUrl, usableFile, options);
      })
      .then((result) => {
        if (result.status === 200) {
          onSubmit({
            url: `${CF_DOMAIN(
              !noAuth ? user : { company: { id: noAuthUser } },
            )}${fileName}.${fileType}`,
            name,
            extension: fileType,
            size,
          });
        }
      })
      .catch((error) => {
        console.error('Upload Error:', error);
      })
      .finally(() => {
        pendingUploadsRef.current -= 1;
        if (pendingUploadsRef.current === 0) {
          setLoading(false);
        }
      });
  };
  if (loading) {
    return <Spinner animation="border" variant="primary" />;
  }
  return icon ? (
    <div>
      <div className={styles.header} onClick={handleIconClick}>
        {!!header && (
          <Text noMargin weight="semiBold" textAlign="left">
            {header}
          </Text>
        )}{' '}
        {!disableAddFile && !readOnly && (
          <div className={styles.iconContainer}>
            {!!iconText && (
              <Text noMargin hover size="sm" textAlign="left">
                {iconText}
              </Text>
            )}
            <div className={styles.iconContainer}>
              <input
                type="file"
                accept={'*/*'}
                ref={iconFileInputRef}
                style={{ display: 'none' }}
                multiple={allowMulti}
                onChange={(e) =>
                  Array.from(e.target.files).forEach(handleUpload)
                }
              />
              <Icon
                hover={!loading}
                disabled={loading}
                color={loading ? 'disabled' : iconColor}
                style={{ fontSize: iconSize }}
              >
                {iconImage}
              </Icon>
            </div>
          </div>
        )}
      </div>
      {!!attachments?.length && (
        <div className={styles.attachments}>
          {attachments?.map((a, idx) => {
            return (
              <SimpleFileCard
                key={`${a.id}-${idx}`}
                extension={a.extension || a?.imageUrl?.split('.')?.pop()}
                file={{ ...a, downloadAllowed: true }}
                fileName={a.name ?? `Attachment-${idx + 1}`}
                onDelete={onDelete}
                readOnly={!effectiveCanDelete}
              />
            );
          })}
        </div>
      )}
    </div>
  ) : disableAddFile ? null : (
    <div>
      <Text weight="semiBold">{header}</Text>
      <div className={styles.smallContainer}>
        <input
          type="file"
          accept="*/*"
          ref={fileInputRef}
          style={{ display: 'none' }}
          multiple={allowMulti}
          onChange={(e) => Array.from(e.target.files).forEach(handleUpload)}
        />
        <Icon style={{ fontSize: '2rem' }} className={styles.icon}>
          photo_camera
        </Icon>
        <Button
          className={styles.button}
          variant="secondary"
          outlined
          onClick={handleButtonClick}
          disabled={loading}
          value={
            loading
              ? t(keys.action.LOADING)
              : t(keys.action.SELECT_VARIABLE, {
                  variable: t(keys.common.FILE),
                })
          }
        />
      </div>
    </div>
  );
}
